import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Plus, X } from 'lucide-react';
import { isPlaceholderLocalDetail } from '@pageboss/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// =====================================================================
// LocationEvidencePanel — owner per-area local proof (D1 proof-gate B5).
//
// The owner records ONE real thing per area they serve (a job actually
// done there, a landmark, a real note). This is the input the location-
// page generator derives its area set from (server-authoritative, B4):
// an area with no real detail generates NO page — by design, and the
// panel says so plainly rather than letting the owner find out later.
//
// Mirrors BusinessInfoPanel exactly: GET → controlled rows → whole-set
// PUT on a single Save; empty = "not filled yet", never an error. The
// filler hint uses the SAME predicate the server filters with
// (isPlaceholderLocalDetail from @pageboss/shared) so the panel never
// promises eligibility the generate call would deny. Hints inform —
// they never block saving.
// =====================================================================

interface EvidenceRow {
  area: string;
  detail: string;
}

interface EvidenceResponse {
  entries: EvidenceRow[];
}

const MAX_ENTRIES = 30;

// The textarea mirrors inspector-field's recipe (the app's one multiline
// input style) so form surfaces stay a single visual family.
const detailClasses = cn(
  'w-full px-3 rounded-[var(--radius)]',
  'bg-card border border-border',
  'text-sm text-foreground',
  'placeholder:text-text-tertiary',
  'py-2 leading-relaxed resize-y min-h-[4.5rem]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

type RowState = 'empty' | 'filler' | 'real';

function rowState(row: EvidenceRow): RowState {
  if (row.detail.trim() === '') return 'empty';
  return isPlaceholderLocalDetail(row.detail, row.area) ? 'filler' : 'real';
}

function RowHint({ state }: { state: RowState }) {
  if (state === 'empty') {
    return (
      <p className="text-xs text-text-tertiary">
        Not filled yet — no page for this area until it has a real detail.
      </p>
    );
  }
  if (state === 'filler') {
    return (
      <p className="text-xs text-warning-fg">
        This reads as generic filler — a specific job, street, or landmark is
        what makes this area eligible for a page.
      </p>
    );
  }
  return <p className="text-xs text-success-fg">Counts as real local proof.</p>;
}

export function LocationEvidencePanel({ siteId }: { siteId: string }) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [rows, setRows] = useState<EvidenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const res = await fetch(
      locationApi(businessId, siteId, 'location-evidence'),
    );
    if (res.status === 401) {
      window.location.href = '/login';
      return;
    }
    if (!res.ok) {
      setError("We couldn't load your service areas. Please try again.");
      setLoading(false);
      return;
    }
    const data = (await res.json()) as EvidenceResponse;
    setRows(data.entries);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function setRow(index: number, patch: Partial<EvidenceRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setSaved(false);
  }

  function addRow() {
    setRows((prev) => [...prev, { area: '', detail: '' }]);
    setSaved(false);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setSaved(false);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // Rows without an area name can't be stored (the schema requires one);
      // drop them quietly — they're unfinished, not wrong.
      const entries = rows.filter((r) => r.area.trim() !== '');
      const res = await fetch(
        locationApi(businessId, siteId, 'location-evidence'),
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(entries),
        },
      );
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        setError('Something went wrong saving your changes. Please try again.');
        return;
      }
      const data = (await res.json()) as EvidenceResponse;
      setRows(data.entries);
      setSaved(true);
    } catch {
      setError('Something went wrong saving your changes. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="h-40 animate-pulse rounded-lg bg-muted" />;
  }

  return (
    <div className="space-y-5 rounded-lg border border-border-subtle bg-card p-6">
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Areas you serve — local proof
        </h2>
        <p className="text-sm text-text-secondary">
          One real thing per area: a job you actually did there, a landmark you
          work near, a true note. That's what earns an area its own page —
          areas left blank simply don't get one. Nothing here is ever invented
          for you.
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-md bg-muted px-3 py-2.5 text-sm text-text-secondary">
          No areas yet. Add the places you actually serve — each one you back
          up with a real detail becomes eligible for its own page.
        </p>
      ) : (
        <ul className="space-y-4">
          {rows.map((row, i) => {
            const state = rowState(row);
            return (
              <li
                key={i}
                className="space-y-3 rounded-md border border-border bg-background p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-1.5">
                    <label
                      htmlFor={`evidence-area-${i}`}
                      className="block text-sm font-medium text-foreground"
                    >
                      Area
                    </label>
                    <Input
                      id={`evidence-area-${i}`}
                      value={row.area}
                      placeholder="Clovis"
                      maxLength={80}
                      onChange={(e) => setRow(i, { area: e.target.value })}
                    />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${row.area || 'this area'}`}
                    className="mt-8 rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => removeRow(i)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor={`evidence-detail-${i}`}
                    className="block text-sm font-medium text-foreground"
                  >
                    A real detail from this area
                  </label>
                  <textarea
                    id={`evidence-detail-${i}`}
                    value={row.detail}
                    rows={3}
                    maxLength={600}
                    placeholder="Re-piped the old galvanized lines in the fourplexes off Olive Ave last spring."
                    className={detailClasses}
                    onChange={(e) => setRow(i, { detail: e.target.value })}
                  />
                  <RowHint state={state} />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={rows.length >= MAX_ENTRIES}
          onClick={addRow}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add an area
        </Button>
        {rows.length >= MAX_ENTRIES ? (
          <span className="text-xs text-text-tertiary">
            That's the limit — 30 areas.
          </span>
        ) : null}
        {/* Design audit T8 (A11, one primary per screen): Business info
            carried TWO filled Saves — the canonical NAP one above and this
            one. The NAP save is the screen's primary (it is the record every
            other surface reads); the areas card is a secondary edit, so its
            Save is outline. Same right-aligned footer geometry as the NAP
            card, "Saved" note first, button on the trailing edge. */}
        <div className="flex items-center justify-end gap-3">
          {saved && !busy ? (
            <span className="text-sm text-text-secondary">Saved</span>
          ) : null}
          <Button type="button" variant="outline" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {error ? (
        <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}
